<script setup>
// 产品感知的支持枢纽(nuxt#23)。沿用 company 暗色布局(与 About / 法律页一致),
// 颜色变量(--brand / --tx / --line…)由 company 布局的 .legal-main 继承下来。
definePageMeta({ layout: "company" });

const cdnUrl = __CDN_URL__;

// 帮助主题卡片 —— 链到现有产品/资源(相对路径,dev/test/prod 通用)
const topics = [
  {
    title: "Getting Started",
    desc: "New to SharkFoto? See what the all-in-one Visual AI platform offers and jump in.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M16 8l-2.6 5.4L8 16l2.6-5.4z"/></svg>',
    links: [
      { label: "About SharkFoto", to: "/company/" },
      { label: "Browse Free Tools", to: "/free-tools/" },
      { label: "Try AI Image", to: "/ai/image/text-to-image/" },
    ],
  },
  {
    title: "Plans & Credits",
    desc: "How subscriptions, monthly credits and billing work across SharkFoto.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18M7 15h4"/></svg>',
    links: [
      { label: "Plans & Pricing", to: "/pricing/" },
      { label: "Pricing FAQs", to: "/pricing/" },
    ],
  },
  {
    title: "AI Image, Video & Audio",
    desc: "Generate and edit with AI — text-to-image, video, music, upscaling and effects.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.7 4.8L18.5 9.5 13.7 11.2 12 16l-1.7-4.8L5.5 9.5l4.8-1.7z"/><path d="M19 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/></svg>',
    links: [
      { label: "AI Image", to: "/ai/image/text-to-image/" },
      { label: "AI Video", to: "/ai/video/text-to-video/" },
      { label: "AI Audio", to: "/ai/audio/text-to-music/" },
    ],
  },
  {
    title: "Free Tools",
    desc: "Convert, compress, resize, crop and digitise embroidery — free, in your browser.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
    links: [
      { label: "All Free Tools", to: "/free-tools/" },
      { label: "Supported Formats", to: "/format/" },
    ],
  },
  {
    title: "Account & Refunds",
    desc: "Manage your account, change or cancel your plan, and review our refund terms.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4.5 20a7.5 7.5 0 0 1 15 0"/></svg>',
    links: [
      { label: "Refund Policy", to: "/company/trust/refund-policy/" },
      { label: "Contact Us", to: "/company/contact-us/" },
    ],
  },
  {
    title: "Privacy & Your Files",
    desc: "How we handle uploads — processed securely, never used to train AI, deletable on request.",
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.4-3 8-7 10-4-2-7-5.6-7-10V6z"/><path d="M9 12l2 2 4-4"/></svg>',
    links: [
      { label: "Privacy Policy", to: "/company/trust/privacy/" },
      { label: "Terms of Service", to: "/company/trust/terms/" },
    ],
  },
];

// 常见问题 —— 答案为 HTML(含链接);LD 用纯文本(去标签)
const faqs = [
  {
    q: "Is SharkFoto free to use?",
    a: 'Yes — our <a class="sup-ilink" href="/free-tools/">Free Tools</a> (convert, compress, resize, crop, flip, rotate and more) are free with no sign-up and no watermark. AI features (image, video and audio generation, enhancement and effects) run on credits — start free and <a class="sup-ilink" href="/pricing/">upgrade</a> for more.',
  },
  {
    q: "How do credits work?",
    a: 'AI features use credits per generation. Every plan includes a monthly credit allowance that refreshes each cycle, and you can top up anytime. See <a class="sup-ilink" href="/pricing/">Plans &amp; Pricing</a> for details.',
  },
  {
    q: "What does SharkFoto Pro include?",
    a: 'Pro unlocks monthly AI credits for image, video and music tools, batch processing, premium embroidery formats (PES / DST / EXP), no watermark and priority processing. Compare on the <a class="sup-ilink" href="/pricing/">Pricing</a> page.',
  },
  {
    q: "Which file formats can I convert?",
    a: 'SharkFoto supports 160+ formats — image, camera RAW, vector, video, audio, document and embroidery. Browse the full list on <a class="sup-ilink" href="/format/">Supported Formats</a>.',
  },
  {
    q: "Can I use what I create commercially?",
    a: "Yes. Content you create or process with SharkFoto is yours to use, including for commercial purposes.",
  },
  {
    q: "Do you use my uploads to train AI models?",
    a: "No. Your files are processed only to deliver the result you asked for. We do not use your uploads to train AI models.",
  },
  {
    q: "How long do you keep my files?",
    a: 'Files are processed securely and are not stored long-term. You can request deletion of your data at any time via <a class="sup-ilink" href="/company/contact-us/">Contact Us</a>.',
  },
  {
    q: "How do I cancel my plan or request a refund?",
    a: 'You can manage or cancel your subscription at any time from your account. For refund eligibility, see our <a class="sup-ilink" href="/company/trust/refund-policy/">Refund Policy</a>.',
  },
  {
    q: "How do I reach a human?",
    a: 'Email us through <a class="sup-ilink" href="/company/contact-us/">Contact Us</a> — our team typically replies within 24 hours.',
  },
];

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a.replace(/<[^>]+>/g, "") },
  })),
};

useHead({
  title: "Support — Help with SharkFoto Tools, AI & Account | SharkFoto",
  meta: [
    { name: "description", content: "SharkFoto Support Center — answers about free tools, AI image/video/audio, plans and credits, privacy, refunds and your account. Browse FAQs or contact our team." },
    { property: "og:site_name", content: "SharkFoto" },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: "en_US" },
    { property: "og:image", content: `${cdnUrl}/sharkfoto_og.png` },
    { property: "og:url", content: "https://sharkfoto.com/support/" },
    { property: "og:title", content: "Support | SharkFoto" },
    { property: "og:description", content: "Help with SharkFoto's free tools, AI tools, plans and your account." },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:url", content: "https://sharkfoto.com/support/" },
    { name: "twitter:title", content: "Support | SharkFoto" },
    { name: "twitter:image", content: `${cdnUrl}/sharkfoto_og.png` },
    { name: "twitter:site", content: "@Shark_Foto" },
    { name: "twitter:description", content: "Help with SharkFoto's free tools, AI tools, plans and your account." },
  ],
  link: [{ rel: "canonical", href: "https://sharkfoto.com/support/" }],
  script: [{ type: "application/ld+json", innerHTML: JSON.stringify(faqLd) }],
});
</script>

<template>
  <div class="support-page">
    <!-- Hero -->
    <header class="sup-hero">
      <h1 class="sup-title">How can we help?</h1>
      <p class="sup-lead">
        Find answers about SharkFoto's AI tools, free utilities, plans and your
        account — or reach our team directly.
      </p>
      <div class="sup-actions">
        <a class="sup-btn sup-btn-primary" href="#faqs">Browse FAQs</a>
        <a class="sup-btn sup-btn-ghost" href="/company/contact-us/">Contact us</a>
      </div>
    </header>

    <!-- Topic cards -->
    <section class="sup-cats" aria-label="Help topics">
      <article v-for="t in topics" :key="t.title" class="sup-card">
        <span class="sup-ico" v-html="t.icon" />
        <h2 class="sc-title">{{ t.title }}</h2>
        <p class="sc-desc">{{ t.desc }}</p>
        <ul class="sc-links">
          <li v-for="l in t.links" :key="l.to">
            <a class="sc-link" :href="l.to">{{ l.label }}<span class="sc-arrow" aria-hidden="true">→</span></a>
          </li>
        </ul>
      </article>
    </section>

    <!-- FAQs -->
    <section id="faqs" class="sup-faqs" aria-label="Frequently asked questions">
      <h2 class="sup-h2">Popular questions</h2>
      <details v-for="(f, i) in faqs" :key="i" class="faq-item">
        <summary class="faq-q">
          <span>{{ f.q }}</span>
          <span class="faq-mark" aria-hidden="true"></span>
        </summary>
        <div class="faq-a" v-html="f.a" />
      </details>
    </section>

    <!-- Contact CTA -->
    <section class="sup-contact">
      <h2 class="sup-h2">Still need help?</h2>
      <p class="sup-contact-lead">
        Can't find the answer here? Send us a message — our team typically replies
        within 24 hours.
      </p>
      <a class="sup-btn sup-btn-primary" href="/company/contact-us/">Contact Us</a>
    </section>
  </div>
</template>

<style scoped>
/* 颜色变量从 company 布局 .legal-main 继承(--brand/--tx/--tx-dim/--line…)。
   一律用 .support-page 前缀提高特异性,覆盖布局 prose 的 :deep() 默认。 */
.support-page {
  --brand-2: #6366f1;
  max-width: 100%;
}

/* ---- Hero ---- */
.support-page .sup-hero {
  text-align: center;
  padding: 8px 0 8px;
}
.support-page .sup-title {
  margin: 0 0 14px;
  color: var(--tx);
  font-size: 40px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
}
.support-page .sup-lead {
  max-width: 560px;
  margin: 0 auto 26px;
  color: var(--tx-dim);
  font-size: 17px;
  line-height: 1.6;
}
.support-page .sup-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}
.support-page .sup-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 22px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border: 1px solid transparent;
  transition: transform 0.12s ease, box-shadow 0.18s ease, background 0.18s, border-color 0.18s;
}
.support-page .sup-btn-primary {
  color: #fff;
  background: linear-gradient(135deg, var(--brand), var(--brand-2));
  box-shadow: 0 6px 18px rgba(139, 92, 246, 0.32);
}
.support-page .sup-btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(139, 92, 246, 0.42);
}
.support-page .sup-btn-ghost {
  color: var(--tx);
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--line-strong);
}
.support-page .sup-btn-ghost:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--brand);
}

/* ---- Topic cards ---- */
.support-page .sup-cats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(330px, 1fr));
  gap: 16px;
  margin: 44px 0 8px;
}
.support-page .sup-card {
  padding: 22px 22px 20px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.025);
  transition: border-color 0.18s, background 0.18s, transform 0.12s;
}
.support-page .sup-card:hover {
  border-color: rgba(139, 92, 246, 0.5);
  background: rgba(139, 92, 246, 0.05);
  transform: translateY(-2px);
}
.support-page .sup-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-bottom: 14px;
  border-radius: 11px;
  color: var(--brand-hover);
  background: rgba(139, 92, 246, 0.12);
}
.support-page .sup-ico :deep(svg) {
  width: 22px;
  height: 22px;
}
.support-page .sc-title {
  margin: 0 0 6px;
  color: var(--tx);
  font-size: 18px;
  font-weight: 650;
  line-height: 1.3;
}
.support-page .sc-desc {
  margin: 0 0 14px;
  color: var(--tx-dim);
  font-size: 14.5px;
  line-height: 1.55;
}
.support-page .sc-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.support-page .sc-links li {
  margin: 0;
}
.support-page .sc-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-hover);
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  border: 0;
  transition: gap 0.15s, color 0.15s;
}
.support-page .sc-link:hover {
  color: #c4b1ff;
  gap: 10px;
}
.support-page .sc-arrow {
  font-size: 13px;
  opacity: 0.8;
}

/* ---- FAQs ---- */
.support-page .sup-faqs {
  margin: 56px 0 0;
  scroll-margin-top: 90px;
}
.support-page .sup-h2 {
  margin: 0 0 20px;
  color: var(--tx);
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.support-page .faq-item {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.025);
  margin-bottom: 10px;
  overflow: hidden;
  transition: border-color 0.18s;
}
.support-page .faq-item[open] {
  border-color: rgba(139, 92, 246, 0.4);
}
.support-page .faq-q {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  cursor: pointer;
  color: var(--tx);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.45;
  list-style: none;
}
.support-page .faq-q::-webkit-details-marker {
  display: none;
}
.support-page .faq-mark {
  position: relative;
  flex: none;
  width: 16px;
  height: 16px;
}
.support-page .faq-mark::before,
.support-page .faq-mark::after {
  content: "";
  position: absolute;
  background: var(--brand-hover);
  border-radius: 2px;
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.support-page .faq-mark::before {
  top: 7px;
  left: 0;
  width: 16px;
  height: 2px;
}
.support-page .faq-mark::after {
  top: 0;
  left: 7px;
  width: 2px;
  height: 16px;
}
.support-page .faq-item[open] .faq-mark::after {
  transform: rotate(90deg);
  opacity: 0;
}
.support-page .faq-a {
  padding: 0 18px 18px;
  color: var(--tx-dim);
  font-size: 15px;
  line-height: 1.65;
}
.support-page .faq-a :deep(a),
.support-page .sup-ilink {
  color: var(--brand-hover);
  text-decoration: none;
  border-bottom: 1px solid rgba(139, 92, 246, 0.4);
  transition: color 0.15s, border-color 0.15s;
}
.support-page .faq-a :deep(a:hover) {
  color: #c4b1ff;
  border-bottom-color: #c4b1ff;
}

/* ---- Contact CTA ---- */
.support-page .sup-contact {
  margin: 56px 0 8px;
  padding: 36px 28px;
  text-align: center;
  border: 1px solid rgba(139, 92, 246, 0.28);
  border-radius: 18px;
  background:
    radial-gradient(120% 140% at 50% 0%, rgba(139, 92, 246, 0.14), transparent 70%),
    rgba(255, 255, 255, 0.02);
}
.support-page .sup-contact .sup-h2 {
  margin-bottom: 10px;
}
.support-page .sup-contact-lead {
  max-width: 480px;
  margin: 0 auto 22px;
  color: var(--tx-dim);
  font-size: 15.5px;
  line-height: 1.6;
}

@media (max-width: 640px) {
  .support-page .sup-title {
    font-size: 31px;
  }
  .support-page .sup-cats {
    grid-template-columns: 1fr;
  }
  .support-page .sup-contact {
    padding: 28px 20px;
  }
}
</style>
